/**
 * Default Scoring Configuration
 *
 * This configuration defines how skiing quality scores are calculated.
 * All values can be overridden via the debug UI.
 */

export const DEFAULT_SCORING_CONFIG = {
  version: 1,

  // Metric definitions with sweet spots and ranges
  // Order matches UI: Air Temperature, Sky Clear, Precipitation, Wind, Snow Depth, New Snow
  metrics: {
    temperature: {
      id: 'temperature',
      name: 'Air Temperature',
      icon: 'thermostat',
      unit: '°F',
      behavior: 'sweet_spot',  // 'sweet_spot' | 'more_is_better' | 'less_is_better'
      description: 'Sweet spot',
      range: { min: -15, max: 60 },
      sweetSpot: { min: 12, max: 30 },
      scoring: [
        { threshold: -10, score: 0, label: 'Brutally Cold' },
        { threshold: 0, score: 3, label: 'Very Cold' },
        { threshold: 5, score: 6, label: 'Brisk' },
        { threshold: 12, score: 8, label: 'Cool' },
        { threshold: 30, score: 10, label: 'Peak Comfort' },
        { threshold: 34, score: 9, label: 'Mild' },
        { threshold: 42, score: 7, label: 'Warm' },
        { threshold: 50, score: 4, label: 'Spring Skiing' },
        { threshold: Infinity, score: 0, label: 'Too Warm' }
      ]
    },
    skyClarity: {
      id: 'skyClarity',
      name: 'Sky Clear',
      icon: 'visibility',
      unit: '%',
      behavior: 'more_is_better',
      description: 'More is better',
      range: { min: 0, max: 100 },
      sweetSpot: { min: 70, max: 100 },
      // Note: skyClarity is 100 - cloudCover, so higher is better
      scoring: [
        { threshold: 80, score: 10, label: 'Clear Skies' },
        { threshold: 30, score: 7, label: 'Broken Clouds' },
        { threshold: 0, score: 5, label: 'Overcast' }
      ]
    },
    precipitation: {
      id: 'precipitation',
      name: 'Precipitation',
      icon: 'water_drop',
      unit: '"',
      behavior: 'less_is_better',
      description: 'Less is better',
      range: { min: 0, max: 2 },
      sweetSpot: { min: 0, max: 0.05 },
      scoring: [
        { threshold: 0, score: 10, label: 'Dry' },
        { threshold: 0.05, score: 8, label: 'Trace' },
        { threshold: 0.15, score: 5, label: 'Light' },
        { threshold: 0.3, score: 3, label: 'Moderate' },  // 0.3"+ now yellow zone
        { threshold: 0.6, score: 2, label: 'Heavy' },     // 0.6"+ now red zone
        { threshold: Infinity, score: 1, label: 'Very Heavy' }
      ]
    },
    wind: {
      id: 'wind',
      name: 'Wind',
      icon: 'air',
      unit: 'mph',
      behavior: 'less_is_better',
      description: 'Less is better',
      range: { min: 0, max: 50 },
      sweetSpot: { min: 0, max: 15 },
      scoring: [
        { threshold: 10, score: 10, label: 'Calm' },
        { threshold: 20, score: 6, label: 'Breezy' },
        { threshold: 30, score: 3, label: 'Windy' },
        { threshold: Infinity, score: 0, label: 'Strong Winds' }
      ]
    },
    snowDepth: {
      id: 'snowDepth',
      name: 'Snow Depth',
      icon: 'layers',
      unit: '"',
      behavior: 'more_is_better',
      description: 'More is better',
      range: { min: 0, max: 100 },
      sweetSpot: { min: 40, max: 100 },
      scoring: [
        { threshold: 40, score: 10, label: 'Ideal' },
        { threshold: 30, score: 8, label: 'Good' },
        { threshold: 20, score: 5, label: 'Marginal' },
        { threshold: 12, score: 4, label: 'Poor' },
        { threshold: 6, score: 1, label: 'Very Poor' },
        { threshold: 0, score: 0, label: 'No Coverage' }
      ]
    },
    freshSnow: {
      id: 'freshSnow',
      name: 'New Snow',
      icon: 'ac_unit',
      unit: '"',
      behavior: 'more_is_better',
      description: 'More is better',
      range: { min: 0, max: 24 },
      sweetSpot: { min: 6, max: 24 },
      scoring: [
        { threshold: 12, score: 10, label: 'Deep Powder' },
        { threshold: 6, score: 9, label: 'Powder Day' },
        { threshold: 1, score: 7, label: 'Fresh Snow' },
        { threshold: 0, score: 0, label: 'No Fresh' }
      ]
    }
  },

  // Weights for each skiing mode (must sum to 1.0)
  // Order matches UI: Air Temperature, Sky Clear, Precipitation, Wind, Snow Depth, New Snow
  weights: {
    onPiste: {
      temperature: 0.25,
      skyClarity: 0.10,
      precipitation: 0.35,
      wind: 0.20,
      snowDepth: 0.10,
      freshSnow: 0.00  // Fresh snow doesn't matter for groomed runs
    },
    offPiste: {
      temperature: 0.10,
      skyClarity: 0.05,
      precipitation: 0.20,
      wind: 0.15,
      snowDepth: 0.25,
      freshSnow: 0.25
    },
    // Heli-skiing: Strict weather requirements for helicopter operations
    // Visibility & cloud ceiling are critical, precipitation often grounds ops
    heliSki: {
      temperature: 0.05,   // Secondary concern
      skyClarity: 0.30,    // Hard requirement - VFR visibility needed
      precipitation: 0.35, // Active snowfall often grounds helicopters
      wind: 0.20,          // Low tolerance - helicopters sensitive to gusts
      snowDepth: 0.05,     // Need good base for landing zones
      freshSnow: 0.05      // Fresh powder is the goal
    }
  },

  // Hard caps that limit max score regardless of weighted average
  hardCaps: {
    onPiste: [
      { metric: 'temperature', threshold: 50, operator: '>', maxScore: 4, label: 'Significant melt conditions' },
      { metric: 'temperature', threshold: 42, operator: '>', maxScore: 6, label: 'Snow softening rapidly' },
      { metric: 'precipitation', threshold: 0.6, operator: '>', maxScore: 3, label: 'Heavy precipitation' },
      { metric: 'precipitation', threshold: 0.3, operator: '>', maxScore: 5, label: 'Moderate precipitation' },
      { metric: 'wind', threshold: 40, operator: '>', maxScore: 1, label: 'Lift closure risk' },
      { metric: 'snowDepth', threshold: 12, operator: '<', maxScore: 3, label: 'Thin cover' }
    ],
    offPiste: [
      { metric: 'precipitation', threshold: 0.4, operator: '>', maxScore: 3, label: 'Poor visibility' },
      { metric: 'precipitation', threshold: 0.2, operator: '>', maxScore: 5, label: 'Reduced safety' },
      { metric: 'snowDepth', threshold: 36, operator: '<', maxScore: 3, label: 'Sharky / Dangerous' },
      { metric: 'wind', threshold: 25, operator: '>', maxScore: 4, label: 'Wind slab risk' },
      { metric: 'wind', threshold: 40, operator: '>', maxScore: 1, label: 'Dangerous' },
      { metric: 'temperature', threshold: 34, operator: '>', penalty: 2, label: 'Wet slide risk' }
    ],
    // Heli-skiing: Very strict caps - helicopter operations need clear VFR conditions
    heliSki: [
      // Visibility is a HARD requirement - can't fly in poor visibility
      { metric: 'skyClarity', threshold: 50, operator: '<', maxScore: 1, label: 'IFR conditions - grounded' },
      { metric: 'skyClarity', threshold: 70, operator: '<', maxScore: 3, label: 'Marginal VFR - risky' },
      { metric: 'skyClarity', threshold: 85, operator: '<', maxScore: 6, label: 'Reduced visibility' },
      // Active precipitation often grounds helicopters
      { metric: 'precipitation', threshold: 0.15, operator: '>', maxScore: 2, label: 'Precipitation - likely grounded' },
      { metric: 'precipitation', threshold: 0.05, operator: '>', maxScore: 5, label: 'Light precip - marginal' },
      // Wind: Low tolerance - helicopters sensitive to gusts
      { metric: 'wind', threshold: 30, operator: '>', maxScore: 1, label: 'High winds - grounded' },
      { metric: 'wind', threshold: 20, operator: '>', maxScore: 4, label: 'Gusty - limited ops' },
      { metric: 'wind', threshold: 15, operator: '>', maxScore: 7, label: 'Breezy - caution' },
      // Still need good snow conditions
      { metric: 'snowDepth', threshold: 48, operator: '<', maxScore: 5, label: 'Thin alpine snowpack' }
    ]
  },

  // Fresh snow requirements for epic ratings
  freshSnowRequirements: {
    onPiste: {
      epic: 6,       // 6"+ for 10/10
      // Raised from 1" → 2": under 2" of 48h fresh is a "groomer day", not a
      // true fresh-snow day. Caps overall at maxWithoutFresh (8) so firm+cold+
      // calm days land at 7-8 rather than 9+.
      excellent: 2,
      maxWithoutFresh: 8  // Cap at 8/10 without fresh snow in prior 48h
    },
    offPiste: {
      epic: 6,
      excellent: 1,
      maxWithoutFresh: 7  // Off-piste without fresh snow caps at 7/10
    },
    // Heli-skiing: Weather matters more than snow, but fresh powder is the draw
    heliSki: {
      epic: 12,      // 12"+ for epic - heli terrain needs deep snow
      excellent: 4,  // 4"+ for excellent
      maxWithoutFresh: 6  // Without fresh snow, limited appeal despite good weather
    }
  },

  // Seasonal phases. Treats identical metrics differently in spring/off-season
  // because a 33" base on April 16 is not the same skiing experience as 33" in January.
  // month is 0-indexed (0=Jan). Windows inclusive of start month and day.
  season: {
    springMelt: {
      startMonth: 2, startDay: 15,  // Mar 15
      endMonth: 4,   endDay: 31,    // end of May
      // Spring-specific hard caps prepended to hardCaps per mode
      extraHardCaps: {
        onPiste: [
          { metric: 'temperature', threshold: 36, operator: '>', maxScore: 6, label: 'Spring melt' },
          { metric: 'temperature', threshold: 50, operator: '>', maxScore: 4, label: 'Heavy slush' }
        ],
        offPiste: [
          { metric: 'temperature', threshold: 34, operator: '>', maxScore: 5, label: 'Wet slide risk (spring)' },
          { metric: 'temperature', threshold: 40, operator: '>', maxScore: 3, label: 'Dangerous spring conditions' }
        ]
      },
      // Tighter fresh-snow cap in spring — stale spring snow without fresh isn't "great"
      maxWithoutFresh: {
        onPiste: 6,
        offPiste: 5,
        heliSki: 4
      },
      // Double the melt-risk penalty on temperature metric
      meltRiskPenalty: -2
    },
    offSeason: {
      // Jun 1 – Oct 31: no US/CA/EU resort meaningfully operating on lifts
      startMonth: 5, startDay: 1,  // Jun 1
      endMonth: 9,   endDay: 31    // end of Oct
    }
  },

  // Red/Yellow zone logic - if any metric is in this zone, caps overall score
  zoneLogic: {
    redZone: {
      threshold: 2,     // Score <= 2 is red
      maxOverall: 4     // Caps overall to max 4
    },
    yellowZone: {
      threshold: 4,           // Score <= 4 is yellow
      maxOverall: 6,          // Single yellow metric caps to 6
      mediocreThreshold: 6,   // Score <= 6 is mediocre
      mediocreMaxOverall: 8,  // Single mediocre caps to 8
      multipleYellowCount: 2, // If 2+ metrics in yellow zone (5-7)
      multipleYellowMaxOverall: 7
    }
  }
};

/**
 * Get a metric definition by ID
 */
export function getMetricConfig(metricId, config = DEFAULT_SCORING_CONFIG) {
  return config.metrics[metricId] || null;
}

/**
 * Get weights for a specific mode
 */
export function getWeights(mode, config = DEFAULT_SCORING_CONFIG) {
  return config.weights[mode] || config.weights.onPiste;
}

/**
 * Validate that weights sum to ~1.0
 */
export function validateWeights(weights) {
  const total = Object.values(weights).reduce((sum, w) => sum + w, 0);
  return Math.abs(total - 1.0) < 0.01;
}
